import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router";
import { RouterProvider } from "react-router/dom";

import type { DatasetEvaluatorDetailsLoaderData } from "@phoenix/pages/dataset/evaluators/datasetEvaluatorDetailsLoader";
import { datasetEvaluatorDetailsLoader } from "@phoenix/pages/dataset/evaluators/datasetEvaluatorDetailsLoader";
import { DatasetEvaluatorDetailsPage } from "@phoenix/pages/dataset/evaluators/DatasetEvaluatorDetailsPage";
import { datasetEvaluatorsLoader } from "@phoenix/pages/dataset/evaluators/datasetEvaluatorsLoader";
import { DatasetEvaluatorsPage } from "@phoenix/pages/dataset/evaluators/DatasetEvaluatorsPage";
import {
  EVALUATOR_DETAILS_ROUTE_ID,
  EvaluatorTracePage,
} from "@phoenix/pages/dataset/evaluators/EvaluatorTracePage";
import { EvaluatorsPage } from "@phoenix/pages/evaluators/EvaluatorsPage";
import { evaluatorsPageLoader } from "@phoenix/pages/evaluators/evaluatorsPageLoader";
import { RootLayout } from "@phoenix/pages/RootLayout";
import { settingsPromptsPageLoader } from "@phoenix/pages/settings/prompts/settingsPromptsPageLoader";
import { SettingsAIProvidersPage } from "@phoenix/pages/settings/SettingsAIProvidersPage";
import { settingsAIProvidersPageLoader } from "@phoenix/pages/settings/settingsAIProvidersPageLoader";
import { SettingsAnnotationsPage } from "@phoenix/pages/settings/SettingsAnnotationsPage";
import { settingsAnnotationsPageLoader } from "@phoenix/pages/settings/settingsAnnotationsPageLoader";
import { SettingsDataPage } from "@phoenix/pages/settings/SettingsDataPage";
import { SettingsGeneralPage } from "@phoenix/pages/settings/SettingsGeneralPage";
import { settingsModelsLoader } from "@phoenix/pages/settings/settingsModelsLoader";
import { SettingsModelsPage } from "@phoenix/pages/settings/SettingsModelsPage";
import { SettingsAlertsPage } from "@phoenix/pages/settings/SettingsAlertsPage";
import { settingsAlertsPageLoader } from "@phoenix/pages/settings/settingsAlertsPageLoader";

import type {
  DatasetLoaderData,
  ProjectLoaderData,
  PromptLoaderData,
  SpanPlaygroundPageLoaderData,
} from "./pages";
import {
  AuthenticatedRoot,
  authenticatedRootLoader,
  datasetLoader,
  DatasetPage,
  DatasetsPage,
  datasetVersionsLoader,
  DatasetVersionsPage,
  ErrorElement,
  ExamplePage,
  examplesLoader,
  ExamplesPage,
  ExperimentComparePage,
  ExperimentDetailPage,
  ExperimentsPage,
  ForgotPasswordPage,
  homeLoader,
  LoggedOutPage,
  LoginPage,
  PlaygroundPage,
  playgroundPageLoader,
  ProfilePage,
  ProjectDashboardSummaryPage,
  ProjectIndexPage,
  projectLoader,
  ProjectMetricsPage,
  ProjectPage,
  ProjectSessionsPage,
  ProjectsPage,
  ProjectSpansPage,
  ProjectsRoot,
  ProjectTracesPage,
  PromptConfigPage,
  promptLoader,
  promptsLoader,
  PromptsPage,
  resetPasswordLoader,
  ResetPasswordPage,
  ResetPasswordWithTokenPage,
  SessionPage,
  SettingsDatasetsPage,
  settingsGeneralPageLoader,
  SettingsPage,
  SettingsPromptsPage,
  SpanPlaygroundPage,
  spanPlaygroundPageLoader,
  SupportPage,
  TracePage,
} from "./pages";
import { GraphQLPage } from "./pages/apis/GraphQLPage";
import { RestAPIPage } from "./pages/apis/RestAPIPage";
import { TemplateCategoryPage } from "./pages/templates/TemplateCategoryPage";
import { TemplatesPage } from "./pages/templates/TemplatesPage";
import { TraceTemplatesPage } from "./pages/templates/TraceTemplatesPage";
import { EvaluationActivityPage } from "./pages/templates/EvaluationActivityPage";
import { RedTeamingPage } from "./pages/templates/RedTeamingPage";
import { ScanHistoryPage } from "./pages/templates/ScanHistoryPage";
import { ReportsPage } from "./pages/templates/ReportsPage";
import { DatasetTemplatesPage } from "./pages/templates/DatasetTemplatesPage";
import { GroundTruthEvaluationPage } from "./pages/templates/GroundTruthEvaluationPage";
import { LlmAssistedEvaluationPage } from "./pages/templates/LlmAssistedEvaluationPage";
import { ReasoningEvaluationPage } from "./pages/templates/ReasoningEvaluationPage";
import { RegressionTestingEvaluationPage } from "./pages/templates/RegressionTestingEvaluationPage";
import { OutputDriftEvaluationPage } from "./pages/templates/OutputDriftEvaluationPage";
import { Layout } from "./pages/Layout";
import { layoutLoader } from "./pages/layoutLoader";
import { GlobalDashboardPage } from "./pages/dashboard/GlobalDashboardPage";
import { globalDashboardLoader } from "./pages/dashboard/globalDashboardLoader";
import { SystemHealthPage } from "./pages/system/SystemHealthPage";
import { ProjectConfigPage } from "./pages/project/ProjectConfigPage";
import { ProjectFailureAnalysisPage } from "./pages/project/ProjectFailureAnalysisPage";
import { ProjectRoot } from "./pages/project/ProjectRoot";
import { promptConfigLoader } from "./pages/prompt/promptConfigLoader";
import { PromptIndexPage } from "./pages/prompt/PromptIndexPage";
import { PromptLayout } from "./pages/prompt/PromptLayout";
import { PromptVersionDetailsPage } from "./pages/prompt/PromptVersionDetailsPage";
import { promptVersionLoader } from "./pages/prompt/promptVersionLoader";
import { promptVersionsLoader } from "./pages/prompt/promptVersionsLoader";
import { PromptVersionsPage } from "./pages/prompt/PromptVersionsPage";
import { projectRedirectLoader } from "./pages/redirects/projectRedirectLoader";
import { promptTagRedirectLoader } from "./pages/redirects/promptTagRedirectLoader";
import { sessionRedirectLoader } from "./pages/redirects/sessionRedirectLoader";
import { spanRedirectLoader } from "./pages/redirects/spanRedirectLoader";
import { traceRedirectLoader } from "./pages/redirects/traceRedirectLoader";
import { settingsDataPageLoader } from "./pages/settings/settingsDataPageLoader";
import { sessionLoader } from "./pages/trace/sessionLoader";

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<ErrorElement />} element={<RootLayout />}>
      {/*
        Using /v1/* below redirects all /v1/* routes that don't have a GET method to the root path.
        In particular, this redirects /v1/traces to the root path (/). This route is for the
        OpenTelemetry trace collector, but users sometimes accidentally try to access Phoenix
        through this URL in their browser, leading to confusion. This redirect helps prevent
        those issues by sending them to the main application.
      */}
      <Route path="/v1/*" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LoggedOutPage />} />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
        loader={resetPasswordLoader}
      />
      <Route
        path="/reset-password-with-token"
        element={<ResetPasswordWithTokenPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route element={<AuthenticatedRoot />} loader={authenticatedRootLoader}>
        <Route element={<Layout />} loader={layoutLoader}>
          <Route
            path="/profile"
            handle={{ crumb: () => "profile" }}
            element={<ProfilePage />}
          />
          <Route
            path="/dashboard"
            loader={globalDashboardLoader}
            element={<GlobalDashboardPage />}
          />
          <Route
            path="/system-health"
            element={<SystemHealthPage />}
            handle={{
              crumb: () => "System Health",
            }}
          />
          <Route
            path="/failure-analysis"
            element={<ProjectFailureAnalysisPage />}
          />
          <Route index loader={homeLoader} />
          <Route
            path="/projects"
            handle={{ crumb: () => "Projects" }}
            element={<ProjectsRoot />}
          >
            <Route index element={<ProjectsPage />} />
            <Route path=":projectId/failure-analysis" element={<ProjectFailureAnalysisPage />} />
            <Route path="failure-analysis" element={<ProjectFailureAnalysisPage />} />
            <Route
              path=":projectId"
              loader={projectLoader}
              handle={{
                crumb: (data: ProjectLoaderData) => data?.project?.name,
                copy: (data: ProjectLoaderData) => [
                  {
                    name: "Project Name",
                    value: data?.project?.name,
                    iconKey: "TextOutline" as const,
                  },
                  {
                    name: "Project ID",
                    value: data?.project?.id,
                    iconKey: "IDOutline" as const,
                  },
                ],
              }}
              element={<ProjectRoot />}
            >
              <Route index element={<ProjectIndexPage />} />
              <Route element={<ProjectPage />}>
                <Route path="traces" element={<ProjectTracesPage />}>
                  <Route path=":traceId" element={<TracePage />} />
                </Route>
                <Route path="spans" element={<ProjectSpansPage />}>
                  <Route path=":traceId" element={<TracePage />} />
                </Route>
                <Route path="sessions" element={<ProjectSessionsPage />}>
                  <Route
                    path=":sessionId"
                    element={<SessionPage />}
                    loader={sessionLoader}
                  />
                </Route>
                <Route path="config" element={<ProjectConfigPage />} />
                <Route path="metrics" element={<ProjectMetricsPage />} />
                <Route path="dashboard" element={<ProjectDashboardSummaryPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="/datasets" handle={{ crumb: () => "Datasets" }}>
            <Route index element={<DatasetsPage />} />
            <Route
              path=":datasetId"
              loader={datasetLoader}
              handle={{
                crumb: (data: DatasetLoaderData) => data?.dataset?.name,
                copy: (data: DatasetLoaderData) => [
                  {
                    name: "Dataset Name",
                    value: data?.dataset?.name,
                    iconKey: "TextOutline" as const,
                  },
                  {
                    name: "Dataset ID",
                    value: data?.dataset?.id,
                    iconKey: "IDOutline" as const,
                  },
                ],
              }}
            >
              <Route element={<DatasetPage />} loader={datasetLoader}>
                <Route index element={<ExperimentsPage />} />
                <Route path="experiments" element={<ExperimentsPage />}>
                  <Route
                    path=":experimentId"
                    element={<ExperimentDetailPage />}
                  />
                </Route>
                <Route
                  path="examples"
                  element={<ExamplesPage />}
                  loader={examplesLoader}
                >
                  <Route path=":exampleId" element={<ExamplePage />} />
                </Route>
                <Route
                  path="versions"
                  element={<DatasetVersionsPage />}
                  loader={datasetVersionsLoader}
                />
                <Route
                  path="evaluators"
                  element={<DatasetEvaluatorsPage />}
                  loader={datasetEvaluatorsLoader}
                  handle={{
                    crumb: () => "evaluators",
                  }}
                />
              </Route>
              <Route
                id={EVALUATOR_DETAILS_ROUTE_ID}
                path="evaluators/:evaluatorId"
                element={<DatasetEvaluatorDetailsPage />}
                loader={datasetEvaluatorDetailsLoader}
                handle={{
                  crumb: (data: DatasetEvaluatorDetailsLoaderData) =>
                    data?.evaluatorDisplayName || "evaluator",
                }}
              >
                <Route path=":traceId" element={<EvaluatorTracePage />} />
              </Route>
              <Route
                path="compare"
                element={<ExperimentComparePage />}
                handle={{ crumb: () => "compare" }}
              />
            </Route>
          </Route>
          <Route
            path="/playground"
            handle={{
              crumb: () => "Playground", // TODO: add playground name
            }}
          >
            <Route
              index
              element={<PlaygroundPage />}
              loader={playgroundPageLoader}
            />
            <Route
              path="spans/:spanId"
              element={<SpanPlaygroundPage />}
              loader={spanPlaygroundPageLoader}
              handle={{
                crumb: (data: SpanPlaygroundPageLoaderData) => {
                  if (data?.span.__typename === "Span") {
                    return `span ${data?.span?.spanId}`;
                  }
                  return "span unknown";
                },
              }}
            />
          </Route>
          <Route path="/evaluators" handle={{ crumb: () => "Evaluators" }}>
            <Route
              index
              element={<EvaluatorsPage />}
              loader={evaluatorsPageLoader}
            />
          </Route>
          <Route
            path="/templates"
            element={<TemplatesPage />}
            handle={{ crumb: () => "Templates" }}
          />
          <Route path="/templates/trace" element={<TraceTemplatesPage />} />
          <Route path="/templates/activity" element={<EvaluationActivityPage />} />
          <Route path="/templates/red-teaming" element={<RedTeamingPage />} />
          <Route path="/templates/red_teaming" element={<RedTeamingPage />} />
          <Route path="/templates/security" element={<RedTeamingPage />} />
          <Route path="/templates/security/red-teaming" element={<RedTeamingPage />} />
          <Route path="/red-teaming" element={<RedTeamingPage />} />
          <Route path="/red_teaming" element={<RedTeamingPage />} />
          <Route path="/security/red-teaming" element={<RedTeamingPage />} />
          <Route path="/security/red_teaming" element={<RedTeamingPage />} />
          <Route path="/security/scan-history" element={<ScanHistoryPage />} />
          <Route path="/scan-history" element={<ScanHistoryPage />} />
          <Route path="/security/reports" element={<ReportsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/templates/dataset" element={<DatasetTemplatesPage />} />
          <Route path="/templates/dataset/ground-truth" element={<GroundTruthEvaluationPage />} />
          <Route path="/templates/dataset/llm-assisted" element={<LlmAssistedEvaluationPage />} />
          <Route path="/templates/dataset/reasoning" element={<ReasoningEvaluationPage />} />
          <Route path="/templates/dataset/regression-testing" element={<RegressionTestingEvaluationPage />} />
          <Route path="/templates/dataset/output-drift" element={<OutputDriftEvaluationPage />} />
          <Route path="/templates/:category" element={<TemplateCategoryPage />} />
          <Route
            path="/prompts"
            handle={{
              crumb: () => "Prompts",
            }}
          >
            <Route index element={<PromptsPage />} loader={promptsLoader} />
            <Route
              path=":promptId"
              loader={promptLoader}
              // force this route to always revalidate, preventing stale versions from being
              // displayed when navigating back to the prompt page after gql mutation
              shouldRevalidate={() => true}
              handle={{
                crumb: (data: PromptLoaderData) => {
                  if (data?.prompt?.__typename === "Prompt") {
                    return data?.prompt?.name;
                  }
                  return "prompt unknown";
                },
                copy: (data: PromptLoaderData) => {
                  if (data?.prompt?.__typename === "Prompt") {
                    return [
                      {
                        name: "Prompt Name",
                        value: data.prompt.name,
                        iconKey: "TextOutline" as const,
                      },
                      {
                        name: "Prompt ID",
                        value: data.prompt.id,
                        iconKey: "IDOutline" as const,
                      },
                    ];
                  }
                  return [];
                },
              }}
            >
              <Route element={<PromptLayout />}>
                <Route index element={<PromptIndexPage />} />
                <Route
                  path="versions"
                  loader={promptVersionsLoader}
                  element={<PromptVersionsPage />}
                >
                  <Route
                    path=":versionId"
                    loader={promptVersionLoader}
                    element={<PromptVersionDetailsPage />}
                  />
                </Route>
                <Route
                  path="config"
                  element={<PromptConfigPage />}
                  loader={promptConfigLoader}
                />
              </Route>
            </Route>
          </Route>
          <Route
            path="/apis/rest"
            element={<RestAPIPage />}
            handle={{
              crumb: () => "REST API",
            }}
          />
          <Route
            path="/apis/graphql"
            element={<GraphQLPage />}
            handle={{
              crumb: () => "GraphQL",
            }}
          />
          <Route
            path="/support"
            element={<SupportPage />}
            handle={{
              crumb: () => "Support",
            }}
          />
          <Route
            path="/settings"
            element={<SettingsPage />}
            handle={{
              crumb: () => "Settings",
            }}
          >
            <Route
              path="general"
              loader={settingsGeneralPageLoader}
              element={<SettingsGeneralPage />}
              handle={{
                crumb: () => "General",
              }}
            />
            <Route
              path="providers"
              loader={settingsAIProvidersPageLoader}
              element={<SettingsAIProvidersPage />}
              handle={{
                crumb: () => "AI Providers",
              }}
            />
            <Route
              path="models"
              loader={settingsModelsLoader}
              element={<SettingsModelsPage />}
              handle={{
                crumb: () => "Models",
              }}
            />
            <Route
              path="datasets"
              element={<SettingsDatasetsPage />}
              handle={{
                crumb: () => "Datasets",
              }}
            />
            <Route
              path="annotations"
              loader={settingsAnnotationsPageLoader}
              element={<SettingsAnnotationsPage />}
              handle={{
                crumb: () => "Annotations",
              }}
            />
            <Route
              path="data"
              element={<SettingsDataPage />}
              handle={{
                crumb: () => "Data Retention",
              }}
              loader={settingsDataPageLoader}
            />
            <Route
              path="prompts"
              element={<SettingsPromptsPage />}
              loader={settingsPromptsPageLoader}
              handle={{
                crumb: () => "Prompts",
              }}
            />
            <Route
              path="alerts"
              element={<SettingsAlertsPage />}
              handle={{
                crumb: () => "Alerts",
              }}
              loader={settingsAlertsPageLoader}
            />
          </Route>
          <Route
            path="/redirects/spans/:span_otel_id"
            loader={spanRedirectLoader}
            errorElement={<ErrorElement />}
          />
          <Route
            path="/redirects/traces/:trace_otel_id"
            loader={traceRedirectLoader}
            errorElement={<ErrorElement />}
          />
          <Route
            path="/redirects/sessions/:session_id"
            loader={sessionRedirectLoader}
            errorElement={<ErrorElement />}
          />
          <Route
            path="/redirects/projects/:project_name"
            loader={projectRedirectLoader}
            errorElement={<ErrorElement />}
          />
          <Route
            path="/redirects/prompts/:promptId/tags/:tagName"
            loader={promptTagRedirectLoader}
            errorElement={<ErrorElement />}
          />
        </Route>
      </Route>
    </Route>
  ),
  {
    basename: window.Config.basename,
  }
);

export function AppRoutes() {
  return <RouterProvider router={router} />;
}
